import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "./auth";
import { workspaceRecord } from "@/lib/offline-workspace";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export interface Branch {
  id: number;
  name: string;
  type: "warehouse" | "branch";
  address: string | null;
  phone: string | null;
  isActive: boolean;
}

interface BranchContextType {
  branches: Branch[];
  activeBranchId: number | null;
  activeBranch: Branch | null;
  setActiveBranchId: (id: number | null) => void;
  hasBranches: boolean;
  refreshBranches: () => void;
  branchReady: boolean;
}

const BranchContext = createContext<BranchContextType>({
  branches: [],
  activeBranchId: null,
  activeBranch: null,
  setActiveBranchId: () => {},
  hasBranches: false,
  refreshBranches: () => {},
  branchReady: false,
});

export function BranchProvider({ children }: { children: ReactNode }) {
  const { store, user, offlineRestored } = useAuth();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [activeBranchId, _setActiveBranchId] = useState<number | null>(null);
  const [branchReady, setBranchReady] = useState(false);
  const key = store && user ? `rp_activeBranchId:${store.id}:${user.id}` : "";

  const loadBranches = useCallback(() => {
    if (!store || !user) return;
    if (offlineRestored || !navigator.onLine) {
      void workspaceRecord<Branch[]>(`branches:${store.id}:${user.id}`)
        .then(saved => { if (saved) setBranches(saved); })
        .catch(() => {});
      return;
    }
    fetch(`${BASE}/api/branches`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : []))
      .then((data: Branch[]) => {
        setBranches(data);
        // If stored branch no longer exists, clear it
        _setActiveBranchId(prev => {
          if (prev !== null && !data.find(b => b.id === prev)) {
            try { localStorage.removeItem(key); } catch {}
            return null;
          }
          return prev;
        });
      })
      .catch(() => {});
  }, [store?.id, user?.id, offlineRestored, key]);

  useEffect(() => {
    setBranches([]);
    setBranchReady(false);
    if (!key) return;
    try {
      const stored = localStorage.getItem(key);
      _setActiveBranchId(stored ? Number(stored) : null);
    } catch { _setActiveBranchId(null); }
    setBranchReady(true);
  }, [key]);
  useEffect(() => {
    if (!branchReady) return;
    loadBranches();
    window.addEventListener("online", loadBranches);
    return () => window.removeEventListener("online", loadBranches);
  }, [loadBranches, branchReady]);

  function setActiveBranchId(id: number | null) {
    _setActiveBranchId(id);
    try {
      if (id == null) localStorage.removeItem(key);
      else localStorage.setItem(key, String(id));
    } catch {}
  }

  const activeBranch = branches.find(b => b.id === activeBranchId) ?? null;

  return (
    <BranchContext.Provider
      value={{
        branches,
        activeBranchId,
        activeBranch,
        setActiveBranchId,
        hasBranches: branches.length > 0,
        refreshBranches: loadBranches,
      branchReady,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  return useContext(BranchContext);
}
